import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { translateText } from '../helpers/auto-translate'

/**
 * Hook для автоперевода динамического контента
 */
export const useAutoTranslate = (text, enabled = true) => {
    const { i18n } = useTranslation()
    const [translated, setTranslated] = useState(text)

    useEffect(() => {
        if (!enabled || !text || !text.trim()) {
            setTranslated(text)
            return
        }

        const currentLang = i18n.language || 'ru'

        if (currentLang === 'ru') {
            setTranslated(text)
            return
        }

        const doTranslate = async () => {
            try {
                const result = await translateText(text, currentLang, 'ru')
                setTranslated(result)
            } catch (error) {
                console.error('Auto-translate error:', error)
                setTranslated(text)
            }
        }

        doTranslate()
    }, [text, i18n.language, enabled])

    return translated
}

/**
 * Hook для перевода массива объектов
 */
export const useAutoTranslateList = (items, fields = []) => {
    const { i18n } = useTranslation()
    const [translated, setTranslated] = useState(items)
    const prevSignatureRef = useRef('')
    const prevLangRef = useRef('')

    useEffect(() => {
        if (!items || !items.length || !fields.length) {
            setTranslated(items)
            return
        }

        const currentLang = i18n.language || 'ru'

        if (currentLang === 'ru') {
            setTranslated(items)
            prevSignatureRef.current = ''
            return
        }

        // Создаём сигнатуру только из items (без языка)
        const signature = items.map((i) => i.id || i.name).join(',')

        // Если язык изменился - сбрасываем сигнатуру для форсированного перевода
        if (prevLangRef.current !== currentLang) {
            prevSignatureRef.current = ''
            prevLangRef.current = currentLang
        }

        // Если сигнатура не изменилась И язык тот же - не переводим
        if (prevSignatureRef.current === signature && prevLangRef.current === currentLang) {
            return
        }

        prevSignatureRef.current = signature

        const translateItems = async () => {
            try {
                const promises = items.map(async (item) => {
                    const translatedItem = { ...item }

                    for (const field of fields) {
                        if (item[field] && typeof item[field] === 'string') {
                            try {
                                translatedItem[field] = await translateText(
                                    item[field],
                                    currentLang,
                                    'ru'
                                )
                            } catch (e) {
                                translatedItem[field] = item[field]
                            }
                        }
                    }

                    return translatedItem
                })

                const results = await Promise.all(promises)
                setTranslated(results)
            } catch (error) {
                console.error('Translation list error:', error)
                setTranslated(items)
            }
        }

        translateItems()
    }, [items, fields, i18n.language])

    return translated
}
